// Estimated Body Weight is derived, display-time-only — computed here
// from the athlete's own gear-weight history, never stored, and never
// fed back into weight_lb or athletes.weight_lb / Current Body Weight.

/** Given an athlete's active gear-weight rows and a measurement date,
 * returns the record in effect on that date: the one with the latest
 * effective_date <= date. Returns null if no record applies yet (e.g.
 * the earliest gear-weight entry postdates this measurement). */
export function findApplicableGearWeight(gearWeights, date) {
  if (!gearWeights || !date) return null;
  let best = null;
  for (const g of gearWeights) {
    if (g.effective_date > date) continue;
    if (!best || g.effective_date > best.effective_date) best = g;
  }
  return best;
}

/** Scale weight minus the gear load in effect on that date. Returns null
 * (not shown) rather than a negative/zero figure — that would mean the
 * recorded gear weight doesn't actually make sense against this scale
 * reading, so nothing should be displayed rather than a misleading
 * number. */
export function estimateBodyWeight(scaleWeightLb, gearWeightLb) {
  if (scaleWeightLb == null || gearWeightLb == null) return null;
  const est = Number(scaleWeightLb) - Number(gearWeightLb);
  if (!(est > 0)) return null;
  return Math.round(est * 100) / 100;
}

function latestActiveByGearStatus(measurements, status) {
  let best = null;
  for (const m of measurements || []) {
    if (m.deleted_at != null || m.weight_lb == null || m.weight_gear_status !== status) continue;
    if (
      !best ||
      m.measurement_date > best.measurement_date ||
      (m.measurement_date === best.measurement_date && m.created_at > best.created_at)
    ) {
      best = m;
    }
  }
  return best;
}

/** The single source of truth for "what body weight should the Profile
 * show right now" — a read-model over the same measurements + gearWeights
 * data every screen already has, never a database value of its own.
 * athletes.weight_lb (the server-maintained cache) stays untouched by
 * this: it's still actual no_gear weight, falling back to legacy
 * unknown, and never an estimate — this function is strictly a DISPLAY
 * layer on top of that, used wherever the UI wants the best available
 * interpretation rather than only the strict cache value.
 *
 * Priority, most to least authoritative:
 *   1. latest active no_gear weight                -> no qualifier
 *   2. latest active legacy unknown weight          -> 'unknown'
 *   3. latest active with_gear weight, with a gear-
 *      load record applicable to ITS date           -> 'estimated'
 *      (derived = scale weight - applicable gear load)
 *   4. latest active with_gear weight, no applicable
 *      gear-load record                             -> 'with gear'
 *      (the raw scale reading, so the profile shows a real number
 *      instead of a meaningless dash)
 *   5. nothing available                            -> null
 *
 * An actual no_gear reading always wins over an estimate even if the
 * with_gear reading used for that estimate is more recent — this
 * function only ever looks at with_gear at all once steps 1 and 2 have
 * both come up empty.
 *
 * Returns { value, qualifier } (qualifier is null for the no-qualifier
 * case, one of 'unknown' | 'estimated' | 'with gear' otherwise) or null.
 * Use formatDisplayWeight() to render it. */
export function computeDisplayWeight(measurements, gearWeights) {
  const noGear = latestActiveByGearStatus(measurements, 'no_gear');
  if (noGear) return { value: noGear.weight_lb, qualifier: null };

  const unknown = latestActiveByGearStatus(measurements, 'unknown');
  if (unknown) return { value: unknown.weight_lb, qualifier: 'unknown' };

  const withGear = latestActiveByGearStatus(measurements, 'with_gear');
  if (withGear) {
    const applicableGear = findApplicableGearWeight(gearWeights, withGear.measurement_date);
    const estimated = applicableGear ? estimateBodyWeight(withGear.weight_lb, applicableGear.gear_weight_lb) : null;
    if (estimated != null) return { value: estimated, qualifier: 'estimated' };
    return { value: withGear.weight_lb, qualifier: 'with gear' };
  }

  return null;
}

/** Renders a computeDisplayWeight() result as shown throughout the
 * Profile: "68.6 lb", "68.6 lb (unknown)", "68 lb (estimated)",
 * "72 lb (with gear)", or "—" for null. */
export function formatDisplayWeight(result) {
  if (!result) return '—';
  return result.qualifier ? `${result.value} lb (${result.qualifier})` : `${result.value} lb`;
}
